import type { ArtikelVorschauAntwort } from '../lib/typen';
import { zahl } from '../lib/format';

/**
 * Die Zahlen, die vor jeder Entscheidung sichtbar sein müssen.
 *
 * Der wichtigste Wert ist der Faktor zwischen Datei und Ergebnis: bei uhlsport
 * werden aus 7.351 gefilterten Zeilen 241 Artikel. Stimmt dieser Faktor nicht,
 * gruppiert das Programm nach etwas, das kein Artikel ist — und das sieht man nur
 * hier, nicht in der Artikelliste.
 */
export default function KennzahlenLeiste({ daten }: { daten: ArtikelVorschauAntwort }) {
  const { kennzahlen: k, pruefung: p, bilder: b } = daten;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Kachel label="Artikel" wert={zahl(k.artikel)} betont />
        <Kachel label="Varianten" wert={zahl(k.varianten)} />
        <Kachel label="wird angelegt" wert={zahl(k.neu)} farbe="gruen" />
        <Kachel label="existiert bereits" wert={zahl(k.existiert)} farbe="rot" />
        <Kachel label="nicht prüfbar" wert={zahl(k.ungeprueft)} farbe="gelb" />
      </div>

      <p className="text-sm text-[#1A1A1A]/70">
        {zahl(daten.gelesen)} Zeilen gelesen, {zahl(daten.behalten)} nach den Filtern
        übrig, daraus {zahl(k.artikel)} Artikel — im Schnitt{' '}
        {k.artikel > 0 ? (k.varianten / k.artikel).toFixed(1).replace('.', ',') : '–'}{' '}
        Varianten je Artikel, größte Gruppe {zahl(k.groessteGruppe)}.
      </p>

      <p className="text-sm text-[#1A1A1A]/70">
        Kategorien: {zahl(daten.kategorien.mitKategorie)} Artikel zugeordnet,{' '}
        {zahl(daten.kategorien.ohneKategorie)} ohne
        {daten.kategorien.unsicher > 0
          && `, davon ${zahl(daten.kategorien.unsicher)} mit unsicherer Zuordnung`}
        .
      </p>

      <p className="text-sm text-[#1A1A1A]/70">
        Bilder:{' '}
        {b.ordner == null
          ? 'kein Ordner gewählt — es wurde nicht nachgesehen, die Artikel entstünden ohne Bild.'
          : (
            <>
              {zahl(b.zugeordnet)} Dateien aus „{b.ordner}" würden hochgeladen und{' '}
              {zahl(b.verknuepfungen)}× mit einer Variante verknüpft.{' '}
              {zahl(b.artikelMitBild)} Artikel vollständig,{' '}
              {zahl(b.artikelTeilweise)} teilweise, {zahl(b.artikelOhneBild)} ohne Bild.
            </>
          )}
      </p>

      <p className="text-sm text-[#1A1A1A]/70">
        Bestandsabgleich über {zahl(p.geprueftEans)} EANs: {zahl(p.trefferRegistry)} im
        Nachtsnapshot gefunden
        {p.liveGegenproben > 0 && (
          <>
            , {zahl(p.liveGegenproben)} Artikel live gegengeprüft
            {p.trefferLive > 0 && ` (${zahl(p.trefferLive)} davon erst dort gefunden)`}
          </>
        )}
        {p.snapshotAlterStunden != null
          && ` · Snapshot ${Math.round(p.snapshotAlterStunden)} h alt`}
        .
      </p>

      {(k.ohneSchluessel > 0 || k.doppelteEan > 0 || k.doppelteVariante > 0) && (
        <ul className="text-sm text-amber-800">
          {k.ohneSchluessel > 0 && (
            <li>
              {zahl(k.ohneSchluessel)} Zeilen ohne Gruppierungswert — sie gehören zu
              keinem Artikel und werden nicht angelegt.
            </li>
          )}
          {k.doppelteEan > 0 && (
            <li>{zahl(k.doppelteEan)} Zeilen wegen doppelter EAN verworfen.</li>
          )}
          {k.doppelteVariante > 0 && (
            <li>
              {zahl(k.doppelteVariante)} Zeilen belegen eine Farbe/Größe-Kombination
              doppelt — in Plenty wären sie nicht unterscheidbar.
            </li>
          )}
        </ul>
      )}

      {p.meldungen.map((text, i) => (
        <p key={i} className="text-sm text-amber-800">{text}</p>
      ))}

      {b.meldungen.map((text, i) => (
        <p key={`bild-${i}`} className="text-sm text-amber-800">{text}</p>
      ))}
    </div>
  );
}

function Kachel({
  label, wert, farbe, betont,
}: {
  label: string; wert: string; farbe?: 'gruen' | 'gelb' | 'rot'; betont?: boolean;
}) {
  const rahmen = farbe === 'gruen' ? 'border-green-300 bg-green-50'
    : farbe === 'gelb' ? 'border-amber-300 bg-amber-50'
    : farbe === 'rot' ? 'border-red-300 bg-red-50'
    : betont ? 'border-[#1A1A1A] bg-[#E9FF57]'
    : 'border-[#DAD9D9] bg-white';

  return (
    <div className={`rounded border p-3 ${rahmen}`}>
      <div className="text-2xl font-bold tabular-nums text-[#1A1A1A]">{wert}</div>
      <div className="text-xs text-[#1A1A1A]/70">{label}</div>
    </div>
  );
}
